import { Button, Loader, ScrollView, SearchField } from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import { listVariables } from "graphql/queries";
import { deleteVariable } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import { VariableCreateForm, VariableUpdateForm } from ".";
import { globalStrings } from "config/translations/es";

const Variables = () => {
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "variable:create",
    "variable:update",
    "variable:delete",
  ]);
  const [variables, setVariables] = useState([]);
  const [variablesLoading, setVariablesLoading] = useState(false);
  const [variableDetails, setVariableDetails] = useState();
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    variableId: null,
    variableName: null,
  });
  const [modalVariable, setModalVariable] = useState({
    visible: false,
    variableId: null,
  });
  const columnsMapped = useMemo(
    () =>
      variables.map((item) => ({
        ...item,
        actions: (
          <>
            <Button
              className="px-1"
              children={
                variableDetails?.id === item.id
                  ? strings.actions.hide
                  : strings.actions.show
              }
              variation="link"
              size="small"
              onClick={() => {
                variableDetails?.id === item.id
                  ? setVariableDetails(null)
                  : setVariableDetails(item);
              }}
            />
            {canUpdate && (
              <Button
                className="px-1"
                children={strings.actions.edit}
                variation="link"
                size="small"
                onClick={() => openModal("update", item.id)}
              />
            )}
            {canDelete && (
              <Button
                className="px-1"
                children={strings.actions.delete}
                variation="link"
                size="small"
                onClick={() => {
                  setModalDetails({
                    visible: true,
                    variableId: item.id,
                    variableName: item.name,
                  });
                }}
              />
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [variables, variableDetails?.id]
  );

  const onNext = () => {
    fetchVariables(paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchVariables(previous, false);
  };
  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchVariables(undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) =>
    setModalVariable({ visible: true, action, variableId: id });
  const onSuccessAction = () => {
    setModalVariable({ visible: false });
    fetchVariables();
  };

  async function fetchVariables(token, isNext = true, keywords) {
    try {
      setVariablesLoading(true);
      const variablesData = await API.graphql(
        graphqlOperation(listVariables, {
          limit: !keywords ? 10 : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      name: { contains: key },
                    },
                    {
                      shortName: { contains: key },
                    },
                    {
                      description: { contains: key },
                    },
                  ],
                })),
              }
            : {},
        })
      );
      const variablesItems = variablesData.data.listVariables.items;
      setVariables(variablesItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: variablesData.data.listVariables.nextToken,
        };
      });
      setVariablesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setVariablesLoading(false);
    }
  }
  async function variableDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(deleteVariable, { input: { id } })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.deleteVariable.id;
      setVariables(variables.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        variableId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchVariables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableColumns = [
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "20%" },
    },
    {
      key: "shortName",
      label: strings.table.headers.shortName,
      _style: { width: "15%" },
    },
    {
      key: "unit",
      label: strings.table.headers.unit,
      _style: { width: "15%" },
    },
    {
      key: "description",
      label: strings.table.headers.description,
      _style: { width: "30%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "20%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      {canCreate && (
        <Button
          style={styles.buttonAdd}
          children={strings.actions.addVariable}
          variation="primary"
          onClick={() => openModal("create")}
        />
      )}
      <div className="p-3">
        <div style={styles.searchByKeyword}>
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
        </div>
        {variablesLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
        <CCollapse visible={!!variableDetails?.id} style={styles.collapseCard}>
          <CCard key={variableDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{variableDetails?.name}"
              </h4>
              <p className="text-muted">
                <b>{strings.details.shortName}:</b> {variableDetails?.shortName}
              </p>
              <p className="text-muted">
                <b>{strings.details.description}:</b>{" "}
                {variableDetails?.description}
              </p>
              <p className="text-muted">
                <b>{strings.details.unit}:</b> {variableDetails?.unit}
              </p>
              <p className="text-muted">
                <b>{strings.details.derived}:</b>{" "}
                {variableDetails?.derived
                  ? globalStrings.yes
                  : globalStrings.not}
              </p>
              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {moment(variableDetails?.createdAt).format(
                  "YYYY-MM-DD HH:mm:ss"
                )}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>
      <CModal
        alignment="center"
        visible={modalDetails.visible}
        onClose={() => setModalDetails({ visible: false, variableId: null })}
        backdrop="static"
      >
        <CModalBody>
          <b>
            {strings.formatString(strings.modal.confirmDeleteQuestion, {
              name: modalDetails.variableName,
            })}
          </b>
        </CModalBody>
        <CModalFooter>
          <Button
            size="small"
            onClick={() =>
              setModalDetails({ visible: false, variableId: null })
            }
          >
            {strings.modal.cancelButton}
          </Button>
          <Button
            size="small"
            variation="primary"
            onClick={() => variableDelete(modalDetails.variableId)}
          >
            {strings.modal.confirmButton}
          </Button>
        </CModalFooter>
      </CModal>
      {modalVariable.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalVariable.visible}
          onClose={() => setModalVariable({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalVariable.action === "create"
                ? strings.modalVariable.addTitle
                : strings.modalVariable.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalVariable.action === "create" ? (
                <VariableCreateForm onSuccess={onSuccessAction} />
              ) : (
                <VariableUpdateForm
                  variableId={modalVariable.variableId}
                  onSuccess={onSuccessAction}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Variables",
    successDeleted: "Variable eliminada con éxito",
    actions: {
      addVariable: "Agregar Variable",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    responses: {
      errorFetch: "Error al consultar variables",
    },
    table: {
      headers: {
        name: "Nombre",
        shortName: "Nombre corto",
        unit: "Unidad",
        description: "Descripción",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles de la Variable",
      shortName: "Nombre corto",
      description: "Descripción",
      unit: "Unidad",
      derived: "Derivado",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar la variable {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalVariable: {
      addTitle: "Agregar Variable",
      updateTitle: "Actualizar Variable",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default Variables;
